import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDroplet, FiClock, FiThermometer, FiCheck, FiZap } = FiIcons;

function EspressoExtraction({ 
  onExtractionComplete, 
  isActive, 
  grindQuality = 'medium',
  tampQuality = 'good'
}) {
  const [extractionTime, setExtractionTime] = useState(0);
  const [waterFlow, setWaterFlow] = useState(0);
  const [temperature, setTemperature] = useState(200); // Fahrenheit
  const [volume, setVolume] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionPhase, setExtractionPhase] = useState('pre-infusion');
  const [showInstructions, setShowInstructions] = useState(true);
  const [cremaQuality, setCremaQuality] = useState(0);

  const targetTime = 25000; // 25 seconds for perfect espresso
  const targetVolume = 60; // 60ml for double shot
  const optimalTemp = 200; // 200°F

  useEffect(() => {
    if (isActive) {
      setShowInstructions(true);
      const timer = setTimeout(() => setShowInstructions(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  useEffect(() => {
    let timer;
    if (isExtracting) {
      timer = setInterval(() => {
        setExtractionTime(prev => {
          const newTime = prev + 100;
          
          // Update extraction phases
          if (newTime < 3000) {
            setExtractionPhase('pre-infusion');
            setWaterFlow(20);
          } else if (newTime < 8000) {
            setExtractionPhase('first-drops');
            setWaterFlow(40);
          } else if (newTime < 20000) {
            setExtractionPhase('main-flow');
            setWaterFlow(80);
          } else if (newTime < 25000) {
            setExtractionPhase('final-flow');
            setWaterFlow(60);
          } else {
            setExtractionPhase('over-extracted');
            setWaterFlow(30);
          }

          // Calculate volume based on time and flow
          const volumeIncrease = (waterFlow / 100) * 0.8;
          setVolume(prev => Math.min(prev + volumeIncrease, 80));

          // Calculate crema quality based on grind, tamp, and timing
          const timeScore = Math.max(0, 1 - Math.abs(newTime - targetTime) / targetTime);
          const grindScore = grindQuality === 'perfect' ? 1 : grindQuality === 'good' ? 0.8 : 0.6;
          const tampScore = tampQuality === 'perfect' ? 1 : tampQuality === 'good' ? 0.8 : 0.6;
          
          setCremaQuality(Math.min(100, (timeScore * grindScore * tampScore) * 100));

          return newTime;
        });
      }, 100);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isExtracting, waterFlow, grindQuality, tampQuality]);

  const startExtraction = () => {
    if (!isActive) return;
    
    setIsExtracting(true);
    
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
  };

  const stopExtraction = () => {
    setIsExtracting(false);
    
    if (onExtractionComplete) {
      const quality = calculateExtractionQuality();
      onExtractionComplete({
        time: extractionTime,
        volume: volume,
        quality: quality,
        cremaQuality: cremaQuality,
        phase: extractionPhase
      });
    }
  };

  const calculateExtractionQuality = () => {
    const timeScore = Math.max(0, 1 - Math.abs(extractionTime - targetTime) / targetTime);
    const volumeScore = Math.max(0, 1 - Math.abs(volume - targetVolume) / targetVolume);
    const overallScore = (timeScore + volumeScore) / 2;

    if (overallScore >= 0.9) return 'perfect';
    if (overallScore >= 0.7) return 'good';
    if (overallScore >= 0.5) return 'okay';
    return 'poor';
  };

  const getPhaseColor = () => {
    const colors = {
      'pre-infusion': 'from-blue-400 to-blue-600',
      'first-drops': 'from-amber-600 to-amber-800',
      'main-flow': 'from-amber-700 to-amber-900',
      'final-flow': 'from-amber-800 to-amber-900',
      'over-extracted': 'from-red-500 to-red-700'
    };
    return colors[extractionPhase] || colors['pre-infusion'];
  };

  const getPhaseDescription = () => {
    const descriptions = {
      'pre-infusion': 'Wetting the grounds...',
      'first-drops': 'First drops appearing',
      'main-flow': 'Steady extraction flow',
      'final-flow': 'Finishing extraction',
      'over-extracted': 'Over-extracted!'
    };
    return descriptions[extractionPhase] || '';
  };

  if (!isActive) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">☕</div>
          <h3 className="text-xl font-bold text-gray-800">Espresso Extraction</h3>
          <p className="text-sm text-gray-600">Perfect timing makes perfect espresso</p>
        </div>

        {/* Instructions */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="text-blue-700 text-sm">
                <div className="font-medium mb-1">Target: 25 seconds, 60ml</div>
                <div>Tap to start/stop extraction</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Espresso Machine Visual */}
        <div className="relative mb-6">
          {/* Machine Head */}
          <div className="w-48 h-16 mx-auto bg-gradient-to-b from-gray-700 to-gray-900 rounded-t-xl relative">
            {/* Portafilter */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-8 bg-gradient-to-b from-gray-600 to-gray-800 rounded-b-full">
              {/* Water drops */}
              <AnimatePresence>
                {isExtracting && (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        className={`absolute w-1 h-3 bg-gradient-to-b ${getPhaseColor()} rounded-full`}
                        style={{ left: `${30 + i * 15}%`, bottom: '-12px' }}
                        initial={{ opacity: 0, y: 0 }}
                        animate={{ 
                          opacity: [0, 1, 0],
                          y: [0, 40, 80]
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          delay: i * 0.2
                        }}
                      />
                    ))}
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Espresso Cup */}
          <div className="w-24 h-16 mx-auto mt-8 bg-gradient-to-b from-white to-gray-100 rounded-b-full border-4 border-gray-300 relative overflow-hidden">
            {/* Espresso liquid */}
            <AnimatePresence>
              {volume > 0 && (
                <motion.div
                  className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${getPhaseColor()}`}
                  initial={{ height: 0 }}
                  animate={{ height: `${Math.min((volume / 70) * 100, 90)}%` }}
                  transition={{ duration: 0.5 }}
                />
              )}
            </AnimatePresence>

            {/* Crema layer */}
            {cremaQuality > 20 && (
              <motion.div
                className="absolute top-0 left-0 right-0 bg-gradient-to-b from-amber-200 to-amber-400 opacity-80"
                style={{ height: `${Math.min(cremaQuality / 5, 20)}%` }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
              />
            )}

            {/* Cup handle */}
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-6 border-4 border-gray-300 rounded-r-full" />
          </div>

          {/* Control Button */}
          <motion.button
            className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full shadow-xl ${
              isExtracting 
                ? 'bg-gradient-to-b from-red-400 to-red-600' 
                : 'bg-gradient-to-b from-green-400 to-green-600'
            } text-white font-bold`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={isExtracting ? stopExtraction : startExtraction}
          >
            {isExtracting ? 'STOP' : 'START'}
          </motion.button>
        </div>

        {/* Extraction Stats */}
        <div className="space-y-4">
          {/* Phase Indicator */}
          {isExtracting && (
            <motion.div
              className={`text-center p-3 rounded-lg bg-gradient-to-r ${getPhaseColor()} text-white`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="font-bold capitalize">{extractionPhase.replace('-', ' ')}</div>
              <div className="text-sm opacity-90">{getPhaseDescription()}</div>
            </motion.div>
          )}

          {/* Time Progress */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Extraction Time</span>
              <span className="font-medium">{(extractionTime / 1000).toFixed(1)}s</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                className={`h-3 rounded-full bg-gradient-to-r ${
                  extractionTime < targetTime ? 'from-blue-500 to-green-500' :
                  extractionTime < targetTime * 1.2 ? 'from-green-500 to-yellow-500' :
                  'from-yellow-500 to-red-500'
                }`}
                style={{ width: `${Math.min((extractionTime / (targetTime * 1.5)) * 100, 100)}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0s</span>
              <span className="font-medium">Target: 25s</span>
              <span>35s</span>
            </div>
          </div>

          {/* Volume Progress */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Volume</span>
              <span className="font-medium">{Math.round(volume)}ml</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                className="h-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500"
                style={{ width: `${Math.min((volume / 80) * 100, 100)}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0ml</span>
              <span className="font-medium">Target: 60ml</span>
              <span>80ml</span>
            </div>
          </div>

          {/* Quality Indicators */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-amber-600">{Math.round(cremaQuality)}%</div>
              <div className="text-xs text-gray-600">Crema Quality</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-blue-600">{Math.round(waterFlow)}%</div>
              <div className="text-xs text-gray-600">Flow Rate</div>
            </div>
          </div>

          {/* Completion Status */}
          {extractionTime > 15000 && (
            <motion.div
              className={`text-center p-3 rounded-lg ${
                calculateExtractionQuality() === 'perfect' ? 'bg-green-100 text-green-700' :
                calculateExtractionQuality() === 'good' ? 'bg-blue-100 text-blue-700' :
                calculateExtractionQuality() === 'okay' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="font-bold capitalize">
                {calculateExtractionQuality()} Extraction!
              </div>
              <div className="text-xs mt-1">
                {calculateExtractionQuality() === 'perfect' && 'Perfect timing and volume!'}
                {calculateExtractionQuality() === 'good' && 'Great technique!'}
                {calculateExtractionQuality() === 'okay' && 'Decent shot, keep practicing.'}
                {calculateExtractionQuality() === 'poor' && 'Try adjusting timing or volume.'}
              </div>
            </motion.div>
          )}

          {/* Complete Button */}
          {extractionTime > 20000 && !isExtracting && (
            <motion.button
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-bold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={stopExtraction}
            >
              <SafeIcon icon={FiCheck} className="inline mr-2" />
              Use This Shot ({calculateExtractionQuality()})
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default EspressoExtraction;